import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Grid, List, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getCollectionsQuery, Collection } from '@/graphql/queries/getCollections';
import { CreateCollectionModal } from '@/components/collections/CreateCollectionModal';
import { CollectionCard } from '@/components/collections/CollectionCard';
import { CollectionTable } from '@/components/collections/CollectionTable';
import { useToast } from '@/hooks/use-toast';

const Collections = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { 
    data: collectionsResponse, 
    isLoading, 
    error,
    refetch,
    isError 
  } = useQuery({
    queryKey: ['collections'],
    queryFn: getCollectionsQuery,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 1, // Only retry once to avoid spamming requests
  });

  // Extract collections array from response
  const collections = collectionsResponse?.collections || [];

  // Sort collections: published first (by published_at desc), then unpublished (by updated_at desc)
  const sortedCollections = [...collections].sort((a, b) => {
    // Published collections come first
    if (a.published_at && !b.published_at) return -1;
    if (!a.published_at && b.published_at) return 1;

    // Both published: sort by published_at desc
    if (a.published_at && b.published_at) {
      return new Date(b.published_at).getTime() - new Date(a.published_at).getTime();
    }

    // Both unpublished: sort by updated_at desc
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });

  // Filter collections based on search term
  const filteredCollections = sortedCollections.filter(collection =>
    collection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    collection.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    refetch();
    toast({
      title: "Success",
      description: "Collection created successfully",
    });
  };

  const handleDeleteSuccess = () => {
    refetch();
    toast({
      title: "Success",
      description: "Collection deleted successfully",
    });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Invalidate and refetch all collection-related queries
      await queryClient.invalidateQueries({ queryKey: ['collections'] });
      await queryClient.invalidateQueries({ queryKey: ['collection'] });
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
      
      // Force refetch
      await refetch();
      
      toast({
        title: "Refreshed",
        description: "Collections data has been refreshed",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh collections",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Check if this is a GraphQL endpoint not implemented error
  const isEndpointNotImplemented = error?.message?.includes('400') || error?.message?.includes('collections');
  
  if (error && !isEndpointNotImplemented) {
    return (
      <div className="min-h-screen from-purple-50 to-blue-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Error loading collections</h2>
              <p className="text-gray-600 mb-4">{error instanceof Error ? error.message : 'Unknown error'}</p>
              <Button onClick={() => refetch()}>Try Again</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen from-purple-50 to-blue-50">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Collections</h1>
              <p className="text-gray-600 mt-1">Manage your pricing data collections</p>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleRefresh}
                disabled={isRefreshing || isLoading}
                title="Refresh collections"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                New Collection
              </Button>
            </div>
          </div>

          {/* Filters and Controls */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search collections..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('table')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading collections...</p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && collections.length === 0 && (
            <div className="text-center py-12">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Grid className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No collections yet</h3>
                <p className="text-gray-600 mb-4">
                  Create your first collection to organize pricing data and assets.
                </p>
                {isEndpointNotImplemented && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-amber-800">
                      <strong>⚠️ Backend Setup Required:</strong> The Collections GraphQL resolver is not yet implemented on the backend. 
                      The UI is ready, but you'll need to deploy the collections resolver to start using this feature.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={handleRefresh}
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Check Again
                    </Button>
                  </div>
                )}
                <Button onClick={() => setIsCreateModalOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Collection
                </Button>
              </div>
            </div>
          )}

          {/* Collections Content */}
          {!isLoading && filteredCollections.length > 0 && (
            <>
              {/* Results Summary */}
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {filteredCollections.length} of {collections.length} collections
                </p>
                {!isRefreshing && collections.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleRefresh}
                    className="text-xs"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Refresh
                  </Button>
                )}
              </div>

              {/* Collections Display */}
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredCollections.map((collection) => (
                    <CollectionCard 
                      key={collection.id} 
                      collection={collection}
                      onDeleteSuccess={handleDeleteSuccess}
                    />
                  ))}
                </div>
              ) : (
                <CollectionTable 
                  collections={filteredCollections}
                  onDeleteSuccess={handleDeleteSuccess}
                />
              )}
            </>
          )}

          {/* No Search Results */}
          {!isLoading && collections.length > 0 && filteredCollections.length === 0 && (
            <div className="text-center py-12">
              <div className="max-w-sm mx-auto">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-600 mb-4">
                  Try adjusting your search terms or clear the search to see all collections.
                </p>
                <Button variant="outline" onClick={() => setSearchTerm('')}>
                  Clear Search
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Collection Modal */}
      <CreateCollectionModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default Collections;